import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for managing photo modal state and navigation
 * @param {Object} options - Configuration options
 * @param {boolean} options.closeOnEscape - Whether to close modal on ESC key (default: true)
 * @param {boolean} options.closeOnOverlayClick - Whether to close modal on overlay click (default: true)
 * @param {boolean} options.lockBodyScroll - Whether to lock body scroll when modal is open (default: true)
 * @returns {Object} Photo modal state and control functions
 */
export const usePhotoModal = (options = {}) => {
  const {
    closeOnEscape = true,
    closeOnOverlayClick = true,
    lockBodyScroll = true
  } = options;

  const [isOpen, setIsOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const previouslyFocusedElementRef = useRef(null);
  const modalRef = useRef(null);

  const openModal = (photoArray, initialIndex = 0) => {
    // Store the currently focused element to return focus later
    previouslyFocusedElementRef.current = document.activeElement;
    
    setPhotos(Array.isArray(photoArray) ? photoArray : []);
    setCurrentIndex(Math.max(0, Math.min(initialIndex, (photoArray?.length || 1) - 1)));
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setPhotos([]);
    setCurrentIndex(0);
    
    // Return focus to the previously focused element
    if (previouslyFocusedElementRef.current) {
      previouslyFocusedElementRef.current.focus();
    }
  };

  const goToPhoto = (index) => {
    if (photos.length === 0) return;
    const newIndex = Math.max(0, Math.min(index, photos.length - 1));
    setCurrentIndex(newIndex);
  };

  const goToPrevious = () => {
    if (photos.length === 0) return;
    const newIndex = currentIndex === 0 ? photos.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
  };

  const goToNext = () => {
    if (photos.length === 0) return;
    const newIndex = currentIndex === photos.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
  };

  // Handle ESC key press
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return;

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen, closeOnEscape]);

  // Handle body scroll lock
  useEffect(() => {
    if (!lockBodyScroll) return;

    if (isOpen) {
      // Store original overflow style
      const originalOverflow = document.body.style.overflow;
      
      // Lock body scroll
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore original overflow on cleanup
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen, lockBodyScroll]);

  // Handle focus trapping within modal
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const handleTabKey = (event) => {
      if (event.key !== 'Tab') return;

      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        // Shift + Tab: moving backwards
        if (document.activeElement === firstFocusableElement) {
          event.preventDefault();
          lastFocusableElement.focus();
        }
      } else {
        // Tab: moving forwards
        if (document.activeElement === lastFocusableElement) {
          event.preventDefault();
          firstFocusableElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [isOpen]);

  // Focus first focusable element when modal opens
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const firstFocusableElement = modalRef.current.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (firstFocusableElement) {
        // Small delay to ensure modal is fully rendered
        setTimeout(() => firstFocusableElement.focus(), 100);
      }
    }
  }, [isOpen]);

  const handleOverlayClick = (event) => {
    if (closeOnOverlayClick && event.target === event.currentTarget) {
      closeModal();
    }
  };

  return {
    isOpen,
    photos,
    currentIndex,
    openModal,
    closeModal,
    goToPhoto,
    goToPrevious,
    goToNext,
    modalRef,
    handleOverlayClick
  };
};